import React from "react";
import { Modal, Table } from "react-bootstrap";
import { Skeleton, Tooltip } from "@mui/material";
import { Button } from "../../common/Button";
import {
  formatAuditTimestamp,
  getDeliveryStatusChip,
  truncateError,
} from "./Constants";

// width drives the fixed table layout, so a long error or registry name can no
// longer widen the modal; full text stays reachable through the cell tooltips.
const SUMMARY_COLUMNS = [
  { label: "Registry", width: "16%" },
  { label: "Type", width: "7%", title: "Delivery type" },
  { label: "File", width: "7%", title: "File type" },
  { label: "Status", width: "12%", title: "Latest status" },
  { label: "Retries", width: "7%", title: "Manual retries" },
  { label: "Attempts", width: "8%", title: "Delivery attempts" },
  { label: "Last error", width: "15%" },
  { label: "Completed", width: "18%", title: "Last completed" },
  { label: "Actions", width: "10%" },
];

const isRetryEligible = (row) => row?.latestStatus === "FAILED";

function LatestStatusChip({ latestStatus }) {
  const chip = getDeliveryStatusChip({ status: latestStatus });
  return (
    <span className={chip.className} title={chip.title}>
      {chip.label}
    </span>
  );
}

function EncounterDetailModal({
  show,
  orgId,
  orgName,
  encounterId,
  summaryByRegistry,
  loading,
  retrySubmitting,
  onClose,
  onRetryRegistry,
}) {
  const displayOrg = orgName || orgId || "—";

  const handleClose = () => {
    if (retrySubmitting) return;
    onClose?.();
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop={retrySubmitting ? "static" : true}
      keyboard={!retrySubmitting}
      centered
      dialogClassName="rda-detail-modal-dialog"
      className="rda-detail-modal"
      aria-labelledby="rda-detail-modal-title"
    >
      <Modal.Header closeButton={!retrySubmitting} className="border-0">
        <Modal.Title id="rda-detail-modal-title">
          Report Delivery Summary
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="rda-detail__meta">
          <span>
            <strong>Org:</strong> {displayOrg}
          </span>
          <span>
            <strong>Encounter:</strong>{" "}
            {encounterId ? (
              <a
                className="rda-link-btn"
                href={`/app/encounter-details?encounterId=${encodeURIComponent(
                  encounterId
                )}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {encounterId}
              </a>
            ) : (
              "—"
            )}
          </span>
        </div>

        <div className="rda-table-wrap table-wrap">
          <Table className="rda-table rda-table--summary">
            <colgroup>
              {SUMMARY_COLUMNS.map((col) => (
                <col key={col.label} style={{ width: col.width }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                {SUMMARY_COLUMNS.map((col) => (
                  <th key={col.label} title={col.title}>
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading
                ? [1, 2, 3].map((n) => (
                    <tr key={`sum-sk-${n}`}>
                      {SUMMARY_COLUMNS.map((col) => (
                        <td key={col.label}>
                          <Skeleton animation="wave" />
                        </td>
                      ))}
                    </tr>
                  ))
                : null}
              {!loading && summaryByRegistry?.length > 0
                ? summaryByRegistry.map((row) => {
                    const err = row.lastError || "";
                    const truncated = truncateError(err);
                    const key = `${row.registryId || ""}#${row.fileType || ""}`;
                    const registryLabel =
                      row.registryName || row.registryId || "—";
                    const completedAt = formatAuditTimestamp(row.lastCompletedAt);
                    return (
                      <tr key={key}>
                        <td>{registryLabel}</td>
                        <td>{row.deliveryType || "—"}</td>
                        <td>{row.fileType || "—"}</td>
                        <td>
                          <LatestStatusChip latestStatus={row.latestStatus} />
                        </td>
                        <td>{row.manualRetryCount ?? "—"}</td>
                        <td>{row.deliveryAttemptCount ?? "—"}</td>
                        <td>
                          {err ? (
                            <Tooltip title={err} arrow>
                              <span className="rda-error-cell">{truncated}</span>
                            </Tooltip>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td>{completedAt}</td>
                        <td>
                          {isRetryEligible(row) ? (
                            <Button
                              type="button"
                              variant="secondary"
                              className="rda-retry-btn"
                              disabled={retrySubmitting || loading}
                              onClick={() => onRetryRegistry?.(row)}
                            >
                              Retry
                            </Button>
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    );
                  })
                : null}
              {!loading &&
              (!summaryByRegistry || summaryByRegistry.length === 0) ? (
                <tr>
                  <td colSpan={SUMMARY_COLUMNS.length} className="rda-empty">
                    No registry summary for this encounter
                  </td>
                </tr>
              ) : null}
            </tbody>
          </Table>
        </div>
      </Modal.Body>
      <Modal.Footer className="border-0">
        <Button
          type="button"
          variant="secondary"
          onClick={handleClose}
          disabled={retrySubmitting}
        >
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default EncounterDetailModal;
